import emailjs from '@emailjs/browser';

// EmailJS Configuration
export const EMAIL_JS_CONFIG = {
  SERVICE_ID: process.env.EMAIL_JS_SERVICE_ID || 'service_rini56d',
  PUBLIC_KEY: process.env.EMAIL_JS_PUBLIC_KEY || 'GRJaZraFl4SFyGDc-',
  TEMPLATE_IDS: {
    TOUR:
      process.env.EMAIL_JS_TOUR_TEMPLATE_ID ||
      process.env.EMAIL_JS_BUILDING_TOUR_TEMPLATE_ID ||
      'template_dgant8q',
    ROOM:
      process.env.EMAIL_JS_ROOM_TEMPLATE_ID ||
      process.env.EMAIL_JS_ROOM_RESERVATION_TEMPLATE_ID ||
      'template_ca99sea',
    SOUP:
      process.env.EMAIL_JS_SOUP_TEMPLATE_ID ||
      process.env.EMAIL_JS_SOUP_DELIVERY_TEMPLATE_ID ||
      'template_uys7xm8',
    TOKEN_ALERTS: process.env.EMAIL_JS_TOKEN_ALERT_ID || 'template_bhri4kl',
    GET_CONNECTED:
      process.env.EMAIL_JS_GET_CONNECTED_TEMPLATE_ID ||
      'template_a6tnsbw',
  },
};

// Initialize EmailJS
try {
  emailjs.init(EMAIL_JS_CONFIG.PUBLIC_KEY);
  if (process.env.NODE_ENV === 'development') {
    console.log('EmailJS initialized');
  }
} catch (error) {
  console.error('Failed to initialize EmailJS:', error);
}

// Debug function to check for missing variables
const debugTemplateVariables = (templateParams, templateName) => {
  console.log(`=== DEBUG: ${templateName} Template Variables ===`);
  Object.entries(templateParams).forEach(([key, value]) => {
    console.log(`${key}: "${value}" (length: ${value.length})`);
  });
  console.log('=== END DEBUG ===');
};

// EmailJS Service Functions
export const sendTourRequest = async (formData) => {
  try {
    // Validate required fields - handle both old and new field names for backward compatibility
    const firstName = formData.user_first_name || formData.name?.split(' ')[0] || '';
    const lastName = formData.user_last_name || formData.name?.split(' ').slice(1).join(' ') || '';
    const email = formData.user_email || formData.email || '';
    const phone = formData.user_phone || formData.phone || '';
    
    if (!firstName || !lastName || !email || !phone) {
      console.error('Missing required fields:', { firstName: !!firstName, lastName: !!lastName, email: !!email, phone: !!phone });
      return {
        success: false,
        message: 'Please fill in all required fields.'
      };
    }

    const templateParams = {
      user_first_name: firstName.trim(),
      user_last_name: lastName.trim(),
      user_email: email.trim(),
      user_phone: phone.trim(),
      relationship_to_student: (formData.relationship_to_student || 'N/A').trim(),
      relationship_to_student_other: (formData.relationship_to_student_other || 'N/A').trim(),
      student_type: (formData.student_type || 'N/A').trim(),
      student_type_other: (formData.student_type_other || 'N/A').trim(),
      field_of_interest: (formData.field_of_interest || 'N/A').trim(),
      current_school: (formData.current_school || 'N/A').trim(),
      expected_graduation_year: (formData.expected_graduation_year || 'N/A').trim(),
      student_first_name: (formData.student_first_name || 'N/A').trim(),
      student_last_name: (formData.student_last_name || 'N/A').trim(),
      student_email: (formData.student_email || 'N/A').trim(),
      student_phone: (formData.student_phone || 'N/A').trim(),
      preferred_date: (formData.preferred_date || formData.preferredDate || 'N/A').trim(),
      preferred_time: (formData.preferred_time || formData.preferredTime || 'N/A').trim(),
      group_size: (formData.group_size || formData.groupSize || 'N/A').trim(),
      additional_info: (formData.additional_info || formData.additionalInfo || 'No additional information provided').trim()
    };

    console.log('Sending tour request with params:', templateParams);
    console.log('Using EmailJS config:', {
      SERVICE_ID: EMAIL_JS_CONFIG.SERVICE_ID,
      TEMPLATE_ID: EMAIL_JS_CONFIG.TEMPLATE_IDS.TOUR,
      PUBLIC_KEY: EMAIL_JS_CONFIG.PUBLIC_KEY ? '***' + EMAIL_JS_CONFIG.PUBLIC_KEY.slice(-4) : 'NOT SET'
    });

    // Log each parameter to help debug template issues
    console.log('Template parameters detail:', Object.entries(templateParams).map(([key, value]) => `${key}: "${value}"`));
    
    // Debug template variables
    debugTemplateVariables(templateParams, 'TOUR');

    const response = await emailjs.send(
      EMAIL_JS_CONFIG.SERVICE_ID,
      EMAIL_JS_CONFIG.TEMPLATE_IDS.TOUR,
      templateParams,
      EMAIL_JS_CONFIG.PUBLIC_KEY
    );

    console.log('EmailJS response:', response);

    if (response.status === 200) {
      return {
        success: true,
        message: 'Tour request sent successfully! A staff member will get back to you within 2-3 business days.'
      };
    } else {
      console.error('EmailJS returned non-200 status:', response.status);
      return {
        success: false,
        message: 'Failed to send tour request. Please try again or contact us directly.'
      };
    }
  } catch (error) {
    console.error('Error sending tour request:', error);
    
    // More specific error messages
    if (error.text) {
      console.error('EmailJS error text:', error.text);
      return {
        success: false,
        message: `EmailJS Error: ${error.text}`
      };
    }
    
    if (error.message) {
      console.error('Error message:', error.message);
      return {
        success: false,
        message: `Error: ${error.message}`
      };
    }
    
    return {
      success: false,
      message: 'An error occurred while sending your tour request. Please try again.'
    };
  }
};

export const sendRoomReservation = async (formData) => {
  try {
    // Validate required fields - handle both old and new field names for backward compatibility
    const firstName = formData.user_first_name || formData.name?.split(' ')[0] || '';
    const lastName = formData.user_last_name || formData.name?.split(' ').slice(1).join(' ') || '';
    const email = formData.user_email || formData.email || '';
    const phone = formData.user_phone || formData.phone || '';
    
    if (!firstName || !lastName || !email || !phone) {
      console.error('Missing required fields for room reservation:', { firstName: !!firstName, lastName: !!lastName, email: !!email, phone: !!phone });
      return {
        success: false,
        message: 'Please fill in all required fields.'
      };
    }

    const templateParams = {
      user_first_name: firstName.trim(),
      user_last_name: lastName.trim(),
      user_email: email.trim(),
      user_phone: phone.trim(),
      organization: (formData.organization || 'N/A').trim(),
      event_type: (formData.event_type || formData.eventType || 'N/A').trim(),
      event_type_other: (formData.event_type_other || 'N/A').trim(),
      facility_requested: (formData.facility_requested || formData.facilityRequested || 'N/A').trim(),
      event_date: (formData.event_date || formData.eventDate || 'N/A').trim(),
      event_time: (formData.event_time || formData.eventTime || 'N/A').trim(),
      expected_attendance: (formData.expected_attendance || formData.expectedAttendance || 'N/A').trim(),
      additional_requirements: (formData.additional_requirements || formData.additionalRequirements || 'None').trim(),
      additional_info: (formData.additional_info || formData.additionalInfo || 'None').trim()
    };

    console.log('Sending room reservation with params:', templateParams);
    console.log('Using EmailJS config:', {
      SERVICE_ID: EMAIL_JS_CONFIG.SERVICE_ID,
      TEMPLATE_ID: EMAIL_JS_CONFIG.TEMPLATE_IDS.ROOM,
      PUBLIC_KEY: EMAIL_JS_CONFIG.PUBLIC_KEY ? '***' + EMAIL_JS_CONFIG.PUBLIC_KEY.slice(-4) : 'NOT SET'
    });

    // Log each parameter to help debug template issues
    console.log('Template parameters detail:', Object.entries(templateParams).map(([key, value]) => `${key}: "${value}"`));
    
    // Debug template variables
    debugTemplateVariables(templateParams, 'ROOM');

    const response = await emailjs.send(
      EMAIL_JS_CONFIG.SERVICE_ID,
      EMAIL_JS_CONFIG.TEMPLATE_IDS.ROOM,
      templateParams,
      EMAIL_JS_CONFIG.PUBLIC_KEY
    );

    console.log('EmailJS response for room reservation:', response);

    if (response.status === 200) {
      return {
        success: true,
        message: 'Room reservation request sent successfully! A staff member will get back to you within 2-3 business days.'
      };
    } else {
      console.error('EmailJS returned non-200 status for room reservation:', response.status);
      return {
        success: false,
        message: 'Failed to send room reservation request. Please try again or contact us directly.'
      };
    }
  } catch (error) {
    console.error('Error sending room reservation:', error);
    
    // More specific error messages
    if (error.text) {
      console.error('EmailJS error text for room reservation:', error.text);
      return {
        success: false,
        message: `EmailJS Error: ${error.text}`
      };
    }
    
    if (error.message) {
      console.error('Error message for room reservation:', error.message);
      return {
        success: false,
        message: `Error: ${error.message}`
      };
    }
    
    return {
      success: false,
      message: 'An error occurred while sending your room reservation request. Please try again.'
    };
  }
};

export const sendSoupRequest = async (formData) => {
  try {
    // Validate required fields - handle both old and new field names for backward compatibility
    const firstName = formData.user_first_name || formData.name?.split(' ')[0] || '';
    const lastName = formData.user_last_name || formData.name?.split(' ').slice(1).join(' ') || '';
    const email = formData.user_email || formData.email || '';
    const phone = formData.user_phone || formData.phone || '';
    const deliveryAddress = formData.delivery_address || formData.deliveryAddress || '';
    const reason = formData.reason || '';
    const preferredDeliveryTime = formData.preferred_delivery_time || formData.preferredDeliveryTime || '';
    const relationshipToStudent = formData.relationship_to_student || '';
    
    // Check all required fields
    const missingFields = [];
    if (!firstName.trim()) missingFields.push('First Name');
    if (!lastName.trim()) missingFields.push('Last Name');
    if (!email.trim()) missingFields.push('Email');
    if (!phone.trim()) missingFields.push('Phone');
    if (!deliveryAddress.trim()) missingFields.push('Delivery Address');
    if (!reason.trim()) missingFields.push('Reason for Request');
    if (!preferredDeliveryTime.trim()) missingFields.push('Preferred Delivery Time');
    if (!relationshipToStudent.trim()) missingFields.push('Relationship to Student');
    
    // Check conditional required fields
    if (relationshipToStudent === 'Other' && !formData.relationship_to_student_other?.trim()) {
      missingFields.push('Please Specify Relationship');
    }
    if (reason === 'Other' && !formData.reason_other?.trim()) {
      missingFields.push('Please Specify Reason');
    }
    
    if (missingFields.length > 0) {
      console.error('Missing required fields for soup request:', missingFields);
      return {
        success: false,
        message: `Please fill in all required fields: ${missingFields.join(', ')}`
      };
    }

    const templateParams = {
      user_first_name: firstName.trim(),
      user_last_name: lastName.trim(),
      user_email: email.trim(),
      user_phone: phone.trim(),
      relationship_to_student: relationshipToStudent.trim(),
      relationship_to_student_other: (formData.relationship_to_student_other || 'N/A').trim(),
      student_first_name: (formData.student_first_name || 'N/A').trim(),
      student_last_name: (formData.student_last_name || 'N/A').trim(),
      student_email: (formData.student_email || 'N/A').trim(),
      student_phone: (formData.student_phone || 'N/A').trim(),
      delivery_address: deliveryAddress.trim(),
      reason: reason.trim(),
      reason_other: (formData.reason_other || 'N/A').trim(),
      preferred_delivery_time: preferredDeliveryTime.trim(),
      dietary_restrictions: (formData.dietary_restrictions || formData.dietaryRestrictions || 'None').trim(),
      additional_info: (formData.additional_info || formData.additionalInfo || 'None').trim()
    };

    console.log('Sending soup request with params:', templateParams);
    console.log('Using EmailJS config:', {
      SERVICE_ID: EMAIL_JS_CONFIG.SERVICE_ID,
      TEMPLATE_ID: EMAIL_JS_CONFIG.TEMPLATE_IDS.SOUP,
      PUBLIC_KEY: EMAIL_JS_CONFIG.PUBLIC_KEY ? '***' + EMAIL_JS_CONFIG.PUBLIC_KEY.slice(-4) : 'NOT SET'
    });

    // Log each parameter to help debug template issues
    console.log('Template parameters detail:', Object.entries(templateParams).map(([key, value]) => `${key}: "${value}"`));
    
    // Debug template variables
    debugTemplateVariables(templateParams, 'SOUP');

    const response = await emailjs.send(
      EMAIL_JS_CONFIG.SERVICE_ID,
      EMAIL_JS_CONFIG.TEMPLATE_IDS.SOUP,
      templateParams,
      EMAIL_JS_CONFIG.PUBLIC_KEY
    );

    console.log('EmailJS response for soup request:', response);

    if (response.status === 200) {
      return {
        success: true,
        message: 'Soup delivery request sent successfully! A staff member will get back to you within 24 hours.'
      };
    } else {
      console.error('EmailJS returned non-200 status for soup request:', response.status);
      return {
        success: false,
        message: 'Failed to send soup delivery request. Please try again or contact us directly.'
      };
    }
  } catch (error) {
    console.error('Error sending soup request:', error);
    
    // More specific error messages
    if (error.text) {
      console.error('EmailJS error text for soup request:', error.text);
      return {
        success: false,
        message: `EmailJS Error: ${error.text}`
      };
    }
    
    if (error.message) {
      console.error('Error message for soup request:', error.message);
      return {
        success: false,
        message: `Error: ${error.message}`
      };
    }
    
    return {
      success: false,
      message: 'An error occurred while sending your soup delivery request. Please try again.'
    };
  }
};

export const sendGetConnectedRequest = async (formData) => {
  try {
    const firstName = formData.user_first_name || '';
    const lastName = formData.user_last_name || '';
    const email = formData.user_email || '';
    const phone = formData.user_phone || '';
    const userType = formData.user_type || '';
    
    if (!firstName || !lastName || !email || !phone || !userType) {
      console.error('Missing required fields for get connected request');
      return {
        success: false,
        message: 'Please fill in all required fields.'
      };
    }

    // Process checkbox arrays into comma-separated strings
    const interestsSelected = Array.isArray(formData.interests) 
      ? formData.interests.join(', ') 
      : (formData.interests || 'None selected');
    
    const groupsSelected = Array.isArray(formData.student_groups) 
      ? formData.student_groups.join(', ') 
      : (formData.student_groups || 'None selected');

    const templateParams = {
      user_first_name: firstName.trim(),
      user_last_name: lastName.trim(),
      user_email: email.trim(),
      user_phone: phone.trim(),
      user_type: userType.trim(),
      user_type_other: (formData.user_type_other || 'N/A').trim(),
      
      // Parent/Family info
      relationship_to_student: (formData.relationship_to_student || 'N/A').trim(),
      student_first_name: (formData.student_first_name || 'N/A').trim(),
      student_last_name: (formData.student_last_name || 'N/A').trim(),
      student_email: (formData.student_email || 'N/A').trim(),
      student_phone: (formData.student_phone || 'N/A').trim(),
      parent_student_type: (formData.parent_student_type || 'N/A').trim(),
      
      // Graduation year
      student_graduation_year: (formData.student_graduation_year || 'N/A').trim(),
      student_graduation_year_other: (formData.student_graduation_year_other || 'N/A').trim(),
      
      // Personal interests
      personal_interests_description: (formData.personal_interests_description || 'None provided').trim(),
      interests_selected: interestsSelected,
      interests_other: (formData.interests_other || 'N/A').trim(),
      
      // Student groups
      student_group_description: (formData.student_group_description || 'None provided').trim(),
      groups_selected: groupsSelected,
      groups_other: (formData.student_groups_other || 'N/A').trim(),
      
      // Connection preferences
      coffee_interest: (formData.coffee_interest || 'N/A').trim(),
      connection_preference: (formData.connection_preference || 'N/A').trim(),
      connection_preference_other: (formData.connection_preference_other || 'N/A').trim(),
      parent_connection_preference: (formData.parent_connection_preference || 'N/A').trim(),
      parent_connection_other: (formData.parent_connection_other || 'N/A').trim(),
      
      // Submission date
      submission_date: new Date().toLocaleString()
    };

    console.log('Sending Get Connected request with params:', templateParams);
    
    debugTemplateVariables(templateParams, 'GET_CONNECTED');

    const response = await emailjs.send(
      EMAIL_JS_CONFIG.SERVICE_ID,
      EMAIL_JS_CONFIG.TEMPLATE_IDS.GET_CONNECTED,
      templateParams,
      EMAIL_JS_CONFIG.PUBLIC_KEY
    );

    console.log('EmailJS response for Get Connected request:', response);

    if (response.status === 200) {
      return {
        success: true,
        message: 'Your request has been sent successfully! A staff member will reach out to you within 2-3 business days.'
      };
    } else {
      console.error('EmailJS returned non-200 status for Get Connected request:', response.status);
      return {
        success: false,
        message: 'Failed to send your request. Please try again or contact us directly.'
      };
    }
  } catch (error) {
    console.error('Error sending Get Connected request:', error);
    
    if (error.text) {
      console.error('EmailJS error text for Get Connected request:', error.text);
      return {
        success: false,
        message: `EmailJS Error: ${error.text}`
      };
    }
    
    if (error.message) {
      console.error('Error message for Get Connected request:', error.message);
      return {
        success: false,
        message: `Error: ${error.message}`
      };
    }
    
    return {
      success: false,
      message: 'An error occurred while sending your request. Please try again.'
    };
  }
}; 